"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { IoTimeOutline, IoArrowForward } from "react-icons/io5";
import { Product } from '../types';

export default function DealOfTheDayClient({ dealProduct }: { dealProduct: Product | null }) {
    const [timeLeft, setTimeLeft] = useState({
        hours: 12,
        minutes: 45,
        seconds: 30
    });

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
                if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
                if (prev.hours > 0) return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
                return prev; // Timer finished
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    if (!dealProduct) {
        return null;
    }

    // Calculate discount percentage
    const originalPrice = dealProduct.compare_at_price || dealProduct.price;
    const discountedPrice = dealProduct.price;
    const discountPercentage = originalPrice > discountedPrice
        ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
        : 0;

    return (
        <section className="ui-section bg-accent text-white">
            <div className="ui-container">
                <h2 className="mb-5 text-center text-sm font-semibold uppercase tracking-[0.18em] text-primary lg:text-left">Deal of the Day</h2>
                <div className="grid items-center gap-6 lg:grid-cols-2 lg:gap-10">
                    {/* Image Side */}
                    <div className="group relative w-full">
                        <div className="relative h-[260px] w-full rounded-xl overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.18)] sm:h-[340px] lg:h-[380px]">
                            <Image
                                src={(() => {
                                    const src = Array.isArray(dealProduct.image_url) ? dealProduct.image_url[0] : dealProduct.image_url;
                                    return src && src.length > 0 ? src : '/placeholder.png';
                                })()}
                                alt={`Deal of the Day - ${dealProduct.name}`}
                                fill
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                className="object-cover transition-transform duration-300 group-hover:scale-[1.01]"
                            />
                        </div>
                        {discountPercentage > 0 && (
                            <div className="absolute left-3 top-3 z-10 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white">
                                -{discountPercentage}% OFF
                            </div>
                        )}
                    </div>

                    {/* Content Side */}
                    <div className="w-full text-center lg:text-left">
                        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5">
                            <IoTimeOutline className="text-primary" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-orange-100">Limited Time Offer</span>
                        </div>

                        <h2 className="mb-4 text-2xl font-semibold leading-tight md:text-4xl">
                            {dealProduct.name.split(' ').slice(0, -1).join(' ')} <span className="text-primary">{dealProduct.name.split(' ').slice(-1)}</span>
                        </h2>

                        {/* Countdown Timer */}
                        <div className="mb-7 flex justify-center gap-3 lg:justify-start">
                            {[
                                { label: 'Hours', value: timeLeft.hours },
                                { label: 'Mins', value: timeLeft.minutes },
                                { label: 'Secs', value: timeLeft.seconds }
                            ].map((item, idx) => (
                                <div key={idx} className="flex flex-col items-center">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-white/20 bg-white/10 font-mono text-xl font-bold text-orange-100">
                                        {String(item.value).padStart(2, '0')}
                                    </div>
                                    <span className="mt-1.5 text-[10px] uppercase tracking-wider text-gray-300">{item.label}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
                            <Link prefetch={false} href={`/product/${dealProduct.slug}`}>
                                <button className="ui-button-accent rounded-lg px-6">
                                    Claim Offer Now <IoArrowForward />
                                </button>
                            </Link>
                            <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-white">₹{discountedPrice.toLocaleString('en-IN')}</span>
                                {discountPercentage > 0 && (
                                    <span className="text-lg text-gray-400 line-through">₹{originalPrice.toLocaleString('en-IN')}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
